import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  getOrganization,
  updateOrganization,
  deleteOrganization,
  getOrganizationInvites,
  revokeOrganizationInvite,
  generateInviteLink,
  getOrgJiraSettings,
  saveOrgJiraSettings,
  clearOrgJiraSettings,
} from '../utils/organizationsApi';
import {
  getSlackAuthUrl,
  getSlackStatus,
  disconnectSlack,
} from '../utils/slackApi';
import {
  getGithubInstallUrl,
  getGithubStatus,
  disconnectGithub,
} from '../utils/githubApi';
import { getShapeUpSettings, updateShapeUpSettings } from '../utils/shapeUpApi';
import BugSettings from '../components/shapeup/BugSettings';
import DefinitionOfDoneSettings from '../components/shapeup/DefinitionOfDoneSettings';
import HillStatusMap from '../components/shapeup/HillStatusMap';
import IssueTypeMap from '../components/shapeup/IssueTypeMap';
import StakeholderViewsSettings from '../components/shapeup/StakeholderViewsSettings';

/**
 * What a bet channel would end up called, for the field below.
 *
 * A preview, not the real thing: the server slugifies for Slack's rules and works
 * around a name already being taken, so what appears in Slack can differ by a
 * numeric suffix. Close enough to answer "will this read sensibly in my sidebar".
 */
const previewChannelName = (template) => {
  const filled = String(template || '{cycle}-{bet}')
    .replace(/\{cycle\}/g, 'Q3 Cycle 2')
    .replace(/\{bet\}/g, 'Billing import');
  const slug = filled
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return `#${slug || 'bet'}`;
};

const OrganizationSettingsPage = () => {
  const { orgId } = useParams();
  const { refreshOrganizations, clearWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');

  // Slack integration state
  const [slackStatus, setSlackStatus] = useState({ connected: false });
  const [loadingSlackStatus, setLoadingSlackStatus] = useState(true);
  const [connectingSlack, setConnectingSlack] = useState(false);
  const [disconnectingSlack, setDisconnectingSlack] = useState(false);

  // GitHub integration state
  const [githubStatus, setGithubStatus] = useState({ connected: false });
  const [loadingGithubStatus, setLoadingGithubStatus] = useState(true);
  const [connectingGithub, setConnectingGithub] = useState(false);
  const [disconnectingGithub, setDisconnectingGithub] = useState(false);

  // JIRA integration state
  const [jiraSettings, setJiraSettings] = useState({
    isConfigured: false,
    url: '',
    username: '',
    storyPointFieldId: '',
    projectKey: '',
  });
  const [jiraForm, setJiraForm] = useState({
    url: '',
    username: '',
    apiToken: '',
    storyPointFieldId: '',
    projectKey: '',
  });
  const [loadingJiraSettings, setLoadingJiraSettings] = useState(true);
  const [savingJira, setSavingJira] = useState(false);
  const [clearingJira, setClearingJira] = useState(false);

  // Delete dialog
  // Methodology (Scrum vs Shape Up)
  const [methodology, setMethodology] = useState('scrum');
  const [savedMethodology, setSavedMethodology] = useState('scrum');
  const [loadingMethodology, setLoadingMethodology] = useState(true);
  const [savingMethodology, setSavingMethodology] = useState(false);
  const [definitionOfDone, setDefinitionOfDone] = useState([]);
  const [dodStandard, setDodStandard] = useState('');
  const [shapeUpDefaults, setShapeUpDefaults] = useState({ definitionOfDone: [], dodStandard: '' });
  const [savingDod, setSavingDod] = useState(false);
  // How a bet's Slack channel gets named. Draft is separate from saved so the
  // preview updates as you type without saving on every keystroke.
  const [channelTemplate, setChannelTemplate] = useState('');
  const [savedChannelTemplate, setSavedChannelTemplate] = useState('');
  const [savingChannelTemplate, setSavingChannelTemplate] = useState(false);
  // Whether this workspace drives scopes from Jira. Off means scopes, statuses,
  // todos, and comments are authored by hand. Defaults on for every existing org.
  const [jiraEnabled, setJiraEnabled] = useState(true);
  const [savingJiraEnabled, setSavingJiraEnabled] = useState(false);
  // How this workspace handles bugs. Held as one object because the six values
  // are saved together, and the component diffs it to know whether to enable Save.
  const [bugSettings, setBugSettings] = useState(null);
  const [savingBugSettings, setSavingBugSettings] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Which settings group is visible. Keyed by name, not index, so hiding the
  // Shape Up or Danger Zone tab never lands on the wrong panel.
  const [activeTab, setActiveTab] = useState('general');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadingSlackStatus(true);
    setLoadingGithubStatus(true);
    setLoadingJiraSettings(true);
    setError(null);
    try {
      const [orgData, invitesData, slackStatusData, githubStatusData, jiraSettingsData, shapeUpData] = await Promise.all([
        getOrganization(orgId),
        getOrganizationInvites(orgId).catch(() => []),
        getSlackStatus(orgId).catch(() => ({ connected: false })),
        getGithubStatus(orgId).catch(() => ({ connected: false })),
        getOrgJiraSettings(orgId).catch(() => ({ isConfigured: false })),
        getShapeUpSettings(orgId).catch(() => ({ methodology: 'scrum' })),
      ]);
      setMethodology(shapeUpData.methodology || 'scrum');
      setSavedMethodology(shapeUpData.methodology || 'scrum');
      setJiraEnabled(shapeUpData.jiraEnabled !== false);
      setDefinitionOfDone(shapeUpData.definitionOfDone || []);
      setDodStandard(shapeUpData.dodStandard || '');
      setShapeUpDefaults(shapeUpData.defaults || { definitionOfDone: [], dodStandard: '' });
      setChannelTemplate(shapeUpData.slackChannelTemplate || '');
      setSavedChannelTemplate(shapeUpData.slackChannelTemplate || '');
      setBugSettings({
        bugTrackingEnabled: shapeUpData.bugTrackingEnabled !== false,
        bugIssueType: shapeUpData.bugIssueType || '',
        bugJql: shapeUpData.bugJql || '',
        bugDoneStatus: shapeUpData.bugDoneStatus || '',
        bugFadeAfterDays: shapeUpData.bugFadeAfterDays ?? 56,
        emergencyBudgetPerCycle: shapeUpData.emergencyBudgetPerCycle ?? 2,
      });
      setOrganization(orgData);
      setInvites(invitesData);
      setName(orgData.name);
      setLogo(orgData.logo || '');
      setSlackStatus(slackStatusData);
      setGithubStatus(githubStatusData);
      setJiraSettings(jiraSettingsData);
      // Pre-fill form with existing values (except token)
      if (jiraSettingsData.isConfigured) {
        setJiraForm({
          url: jiraSettingsData.url || '',
          username: jiraSettingsData.username || '',
          apiToken: '', // Never pre-fill token for security
          storyPointFieldId: jiraSettingsData.storyPointFieldId || '',
          projectKey: jiraSettingsData.projectKey || '',
        });
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError(err.response?.data?.message || 'Failed to load organization');
    } finally {
      setLoading(false);
      setLoadingSlackStatus(false);
      setLoadingGithubStatus(false);
      setLoadingJiraSettings(false);
      setLoadingMethodology(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for Slack OAuth callback results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const slackConnected = urlParams.get('slack_connected');
    const slackError = urlParams.get('slack_error');

    const githubConnected = urlParams.get('github_connected');
    const githubError = urlParams.get('github_error');

    if (slackConnected === 'true') {
      setSuccessMessage('Slack connected successfully! You can now configure notifications for each team.');
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (slackError) {
      setActionError(`Slack connection failed: ${slackError}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (githubConnected === 'true') {
      setSuccessMessage('GitHub connected successfully! Pull requests will link to scopes automatically by their Jira key.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (githubError) {
      setActionError(`GitHub connection failed: ${githubError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const canManage = organization && ['owner', 'admin'].includes(organization.userRole);
  const isOwner = organization && organization.userRole === 'owner';

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrganization(orgId, { name, logo: logo || null });
      setSuccessMessage('Organization updated successfully');
      await refreshOrganizations();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMethodology = async () => {
    setSavingMethodology(true);
    try {
      const updated = await updateShapeUpSettings(orgId, { methodology });
      setSavedMethodology(updated.methodology);
      setSuccessMessage(
        updated.methodology === 'scrum'
          ? 'Methodology saved. Shape Up is hidden for this organization.'
          : 'Methodology saved. Shape Up is now available in the navigation.'
      );
      // The navbar decides whether to show Shape Up from the cached org list.
      await refreshOrganizations();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to save methodology');
    } finally {
      setSavingMethodology(false);
    }
  };

  const handleSaveDefinitionOfDone = async ({ definitionOfDone: items, dodStandard: nextStandard }) => {
    setSavingDod(true);
    try {
      const updated = await updateShapeUpSettings(orgId, {
        definitionOfDone: items,
        dodStandard: nextStandard,
      });
      setDefinitionOfDone(updated.definitionOfDone || []);
      setDodStandard(updated.dodStandard || '');
      setSuccessMessage(
        items.length
          ? `Definition of done saved. Every scope now carries ${items.length} checks.`
          : 'Definition of done cleared.'
      );
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to save the definition of done');
    } finally {
      setSavingDod(false);
    }
  };

  const handleSaveChannelTemplate = async () => {
    setSavingChannelTemplate(true);
    try {
      const updated = await updateShapeUpSettings(orgId, {
        slackChannelTemplate: channelTemplate.trim(),
      });
      setChannelTemplate(updated.slackChannelTemplate || '');
      setSavedChannelTemplate(updated.slackChannelTemplate || '');
      setSuccessMessage(`New bet channels will be named like ${previewChannelName(updated.slackChannelTemplate)}.`);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to save the channel name');
    } finally {
      setSavingChannelTemplate(false);
    }
  };

  const handleSaveBugSettings = async (draft) => {
    setSavingBugSettings(true);
    try {
      const updated = await updateShapeUpSettings(orgId, {
        bugTrackingEnabled: draft.bugTrackingEnabled,
        bugIssueType: draft.bugIssueType,
        bugJql: draft.bugJql,
        bugDoneStatus: draft.bugDoneStatus,
        // Sent as numbers because the fields are text inputs; the server clamps
        // them, and reading the clamped values back is what tells the user their
        // 5000 became 365.
        bugFadeAfterDays: Number(draft.bugFadeAfterDays) || 0,
        emergencyBudgetPerCycle: Number(draft.emergencyBudgetPerCycle) || 0,
      });
      setBugSettings({
        bugTrackingEnabled: updated.bugTrackingEnabled !== false,
        bugIssueType: updated.bugIssueType || '',
        bugJql: updated.bugJql || '',
        bugDoneStatus: updated.bugDoneStatus || '',
        bugFadeAfterDays: updated.bugFadeAfterDays ?? 56,
        emergencyBudgetPerCycle: updated.emergencyBudgetPerCycle ?? 2,
      });
      setSuccessMessage(
        updated.bugTrackingEnabled === false
          ? 'Bug pool turned off. Nothing was deleted.'
          : 'Bug settings saved.'
      );
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to save the bug settings');
    } finally {
      setSavingBugSettings(false);
    }
  };

  const handleToggleJiraEnabled = async (nextEnabled) => {
    // Optimistic: the Switch flips immediately, and we roll it back on failure so
    // it never lies about the saved state.
    setJiraEnabled(nextEnabled);
    setSavingJiraEnabled(true);
    try {
      const updated = await updateShapeUpSettings(orgId, { jiraEnabled: nextEnabled });
      setJiraEnabled(updated.jiraEnabled !== false);
      setSuccessMessage(
        nextEnabled
          ? 'Jira integration on. Scopes sync from your Jira issues again.'
          : 'Jira integration off. Scopes, statuses, and todos are now authored by hand. '
            + 'Your Jira connection and existing data are kept.'
      );
    } catch (err) {
      setJiraEnabled(!nextEnabled);
      setActionError(err.response?.data?.message || 'Failed to change the Jira integration setting');
    } finally {
      setSavingJiraEnabled(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== organization.name) return;

    setDeleting(true);
    try {
      await deleteOrganization(orgId);
      clearWorkspace();
      await refreshOrganizations();
      navigate('/organizations');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete organization');
      setDeleting(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await revokeOrganizationInvite(orgId, inviteId);
      setSuccessMessage('Invite revoked');
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to revoke invite');
    }
  };

  const handleCopyInviteLink = (token) => {
    navigator.clipboard.writeText(generateInviteLink(token));
    setSuccessMessage('Invite link copied to clipboard!');
  };

  const handleConnectSlack = async () => {
    setConnectingSlack(true);
    try {
      const { authUrl } = await getSlackAuthUrl(orgId);
      // Redirect to Slack OAuth
      window.location.href = authUrl;
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to initiate Slack connection');
      setConnectingSlack(false);
    }
  };

  const handleDisconnectSlack = async () => {
    setDisconnectingSlack(true);
    try {
      await disconnectSlack(orgId);
      setSlackStatus({ connected: false });
      setSuccessMessage('Slack disconnected successfully');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to disconnect Slack');
    } finally {
      setDisconnectingSlack(false);
    }
  };

  const handleConnectGithub = async () => {
    setConnectingGithub(true);
    try {
      const { url } = await getGithubInstallUrl(orgId);
      // Redirect to the GitHub App install page; GitHub returns to our callback.
      window.location.href = url;
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to initiate GitHub connection');
      setConnectingGithub(false);
    }
  };

  const handleDisconnectGithub = async () => {
    setDisconnectingGithub(true);
    try {
      await disconnectGithub(orgId);
      setGithubStatus({ connected: false });
      setSuccessMessage('GitHub disconnected successfully');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to disconnect GitHub');
    } finally {
      setDisconnectingGithub(false);
    }
  };

  const handleSaveJira = async () => {
    if (!jiraForm.url || !jiraForm.username || !jiraForm.apiToken) {
      setActionError('JIRA URL, username, and API token are required');
      return;
    }

    setSavingJira(true);
    try {
      const result = await saveOrgJiraSettings(orgId, jiraForm);
      setJiraSettings({
        isConfigured: true,
        url: result.url,
        username: result.username,
        storyPointFieldId: result.storyPointFieldId,
        projectKey: result.projectKey,
      });
      // Clear the token field after save
      setJiraForm(prev => ({ ...prev, apiToken: '' }));
      setSuccessMessage('JIRA settings saved successfully');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to save JIRA settings');
    } finally {
      setSavingJira(false);
    }
  };

  const handleClearJira = async () => {
    setClearingJira(true);
    try {
      await clearOrgJiraSettings(orgId);
      setJiraSettings({ isConfigured: false, url: '', username: '', storyPointFieldId: '', projectKey: '' });
      setJiraForm({ url: '', username: '', apiToken: '', storyPointFieldId: '', projectKey: '' });
      setSuccessMessage('JIRA settings cleared successfully');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to clear JIRA settings');
    } finally {
      setClearingJira(false);
    }
  };

  const getInviteStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'warning';
      case 'revoked': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
      </Container>
    );
  }

  if (!organization || !canManage) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>Access denied</Alert>
      </Container>
    );
  }

  // Tabs are built from what this org actually has: Shape Up only appears when the
  // methodology enables it, and Danger Zone only for owners. Selecting by value
  // (not index) keeps the panels stable as tabs come and go.
  const tabs = [
    { value: 'general', label: 'General' },
    ...(methodology !== 'scrum' ? [{ value: 'shapeup', label: 'Shape Up' }] : []),
    { value: 'invites', label: 'Invites' },
    { value: 'integrations', label: 'Integrations' },
    ...(isOwner ? [{ value: 'danger', label: 'Danger Zone' }] : []),
  ];
  const currentTab = tabs.some((t) => t.value === activeTab) ? activeTab : 'general';

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/organizations" underline="hover" color="inherit">
            Organizations
          </Link>
          <Link component={RouterLink} to={`/organizations/${orgId}`} underline="hover" color="inherit">
            {organization.name}
          </Link>
          <Typography color="text.primary">Settings</Typography>
        </Breadcrumbs>

        <Typography variant="h4" gutterBottom>Organization Settings</Typography>

        <Tabs
          value={currentTab}
          onChange={(e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>

        {currentTab === 'general' && (
          <>
        {/* General Settings */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>General</Typography>
          <TextField
            fullWidth
            label="Organization Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Logo URL"
            value={logo}
            onChange={(e) => setLogo(e.target.value)}
            helperText="Enter a URL to your organization's logo"
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Methodology */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Methodology</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scrum gives you planning poker sessions and story points. Shape Up gives you cycles,
            bets, and hill charts built from your Jira issues. Choosing one hides the other's
            navigation, but past sessions stay open to anyone with the link, so no estimates are
            lost if you switch.
          </Typography>
          {loadingMethodology ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <RadioGroup
                value={methodology}
                onChange={(e) => setMethodology(e.target.value)}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="scrum"
                  control={<Radio />}
                  disabled={!canManage || savingMethodology}
                  label="Scrum only — planning poker and story points"
                />
                <FormControlLabel
                  value="shape-up"
                  control={<Radio />}
                  disabled={!canManage || savingMethodology}
                  label="Shape Up only — cycles, bets, and hill charts, with planning poker hidden"
                />
                <FormControlLabel
                  value="both"
                  control={<Radio />}
                  disabled={!canManage || savingMethodology}
                  label="Both — keep planning poker and add Shape Up"
                />
              </RadioGroup>
              {canManage && (
                <Button
                  variant="contained"
                  onClick={handleSaveMethodology}
                  disabled={savingMethodology || methodology === savedMethodology}
                >
                  {savingMethodology ? <CircularProgress size={24} /> : 'Save Methodology'}
                </Button>
              )}
            </>
          )}
        </Box>

          </>
        )}

        {currentTab === 'shapeup' && methodology !== 'scrum' && (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>Jira integration</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                On, hill charts sync from your Jira issues: scopes, statuses, todos, and
                comments come from Jira. Off, this workspace runs Shape Up on its own —
                scopes are added by hand, their status is set from a dropdown, and todos
                and comments live here. Turning this off keeps your Jira connection and any
                already-synced data; it just stops reading from Jira.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={jiraEnabled}
                    onChange={(e) => handleToggleJiraEnabled(e.target.checked)}
                    disabled={!canManage || savingJiraEnabled}
                  />
                }
                label={jiraEnabled ? 'Sync scopes from Jira' : 'Manual mode — Jira sync off'}
              />
            </Box>

            {jiraEnabled && (
              <>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <IssueTypeMap organizationId={orgId} canManage={canManage} />
                </Box>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <HillStatusMap organizationId={orgId} canManage={canManage} />
                </Box>
              </>
            )}

            <Divider sx={{ my: 4 }} />
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>Definition of done</Typography>
              {loadingMethodology ? (
                <CircularProgress size={24} />
              ) : (
                <DefinitionOfDoneSettings
                  items={definitionOfDone}
                  standard={dodStandard}
                  defaults={shapeUpDefaults}
                  canManage={canManage}
                  saving={savingDod}
                  onSave={handleSaveDefinitionOfDone}
                />
              )}
            </Box>

            <Divider sx={{ my: 4 }} />
            <Box sx={{ mb: 4 }}>
              {loadingMethodology || !bugSettings ? (
                <CircularProgress size={24} />
              ) : (
                <BugSettings
                  value={bugSettings}
                  defaults={shapeUpDefaults}
                  jiraEnabled={jiraEnabled}
                  canManage={canManage}
                  saving={savingBugSettings}
                  onSave={handleSaveBugSettings}
                />
              )}
            </Box>

            <Divider sx={{ my: 4 }} />
            <Box sx={{ mb: 4 }}>
              <StakeholderViewsSettings
                organizationId={orgId}
                canManage={canManage}
                onNotify={(message, severity) => {
                  if (severity === 'error') setActionError(message);
                  else setSuccessMessage(message);
                }}
                onChanged={refreshOrganizations}
              />
            </Box>
          </>
        )}

        {currentTab === 'invites' && (
          <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Invite Links</Typography>
          {invites.length > 0 ? (
            <List>
              {invites.map((invite) => (
                <ListItem key={invite.id}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {invite.token}
                        </Typography>
                        <Chip
                          label={invite.status}
                          size="small"
                          color={getInviteStatusColor(invite.status)}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        Created by {invite.createdBy?.name || 'Unknown'} -
                        Expires {new Date(invite.expiresAt).toLocaleDateString()} -
                        Used {invite.useCount}{invite.maxUses ? `/${invite.maxUses}` : ''} times
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {invite.status === 'active' && (
                      <>
                        <IconButton onClick={() => handleCopyInviteLink(invite.token)}>
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton onClick={() => handleRevokeInvite(invite.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">No invite links created</Typography>
          )}
          </Box>
        )}

        {currentTab === 'integrations' && (
          <>
        {/* Slack Integration */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Slack Integration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Slack workspace to receive notifications when async voting rounds complete without consensus.
            Each team can configure their own notification channel.
          </Typography>

          {loadingSlackStatus ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking Slack connection...</Typography>
            </Box>
          ) : slackStatus.connected ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip label="Connected" color="success" size="small" />
                    <Typography variant="body1">
                      <strong>{slackStatus.slackTeamName}</strong>
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Connected by {slackStatus.installedBy?.name || 'Unknown'} on{' '}
                    {new Date(slackStatus.installedAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDisconnectSlack}
                  disabled={disconnectingSlack}
                >
                  {disconnectingSlack ? <CircularProgress size={24} /> : 'Disconnect'}
                </Button>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                To configure notifications, go to each team's settings page and select the channel for notifications.
              </Alert>
              {/* Slack never grants new permissions to an existing install, so a
                  workspace connected before bet channels shipped has to go through
                  the flow again. Better to say so here than to fail when someone
                  ticks the box on a new bet. */}
              {slackStatus.canCreateChannels === false && (
                <Alert
                  severity="warning"
                  sx={{ mt: 2 }}
                  action={(
                    <Button
                      size="small"
                      color="inherit"
                      onClick={handleConnectSlack}
                      disabled={connectingSlack}
                    >
                      {connectingSlack ? <CircularProgress size={18} color="inherit" /> : 'Reconnect'}
                    </Button>
                  )}
                >
                  This connection cannot create channels yet. Reconnect to let the app
                  open a channel per bet and invite the team to it. Nothing else changes.
                </Alert>
              )}

              {slackStatus.canCreateChannels && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Bet channel names
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Used when a bet gets its own channel. <code>{'{cycle}'}</code> and{' '}
                    <code>{'{bet}'}</code> are filled in, and the result is lowercased with
                    spaces turned into hyphens to satisfy Slack.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      value={channelTemplate}
                      onChange={(e) => setChannelTemplate(e.target.value)}
                      placeholder="{cycle}-{bet}"
                      inputProps={{ maxLength: 80 }}
                      helperText={`Looks like ${previewChannelName(channelTemplate)}`}
                      sx={{ minWidth: 260 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleSaveChannelTemplate}
                      disabled={savingChannelTemplate || channelTemplate.trim() === savedChannelTemplate}
                      sx={{ mt: 0.25 }}
                    >
                      {savingChannelTemplate ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          ) : (
            <Box>
              <Button
                variant="contained"
                onClick={handleConnectSlack}
                disabled={connectingSlack}
                sx={{
                  backgroundColor: '#4A154B',
                  '&:hover': { backgroundColor: '#611f64' },
                }}
              >
                {connectingSlack ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Add to Slack'
                )}
              </Button>
              <Alert severity="info" sx={{ mt: 2 }}>
                Click "Add to Slack" to connect your Slack workspace. After connecting, you can configure
                notification channels for each team in your organization.
              </Alert>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* GitHub Integration */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>GitHub Integration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Install the GitHub App to link pull requests to scopes automatically. A PR whose
            branch, title, or body names a scope's Jira key (e.g. <code>feature/PROJ-123-login</code>)
            shows its state — draft, in review, CI, merged — right on the scope card.
          </Typography>

          {loadingGithubStatus ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking GitHub connection...</Typography>
            </Box>
          ) : githubStatus.connected ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip label="Connected" color="success" size="small" />
                    <Typography variant="body1">
                      <strong>{githubStatus.githubAccountLogin || 'GitHub'}</strong>
                    </Typography>
                  </Box>
                  {githubStatus.installedBy && (
                    <Typography variant="body2" color="text.secondary">
                      Connected by {githubStatus.installedBy?.name || 'Unknown'}
                      {githubStatus.installedAt && (
                        <> on {new Date(githubStatus.installedAt).toLocaleDateString()}</>
                      )}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDisconnectGithub}
                  disabled={disconnectingGithub}
                >
                  {disconnectingGithub ? <CircularProgress size={24} /> : 'Disconnect'}
                </Button>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Pull requests link to scopes automatically by their Jira key. You can also link a PR
                by hand from a scope's detail drawer.
              </Alert>
            </Paper>
          ) : (
            <Box>
              <Button
                variant="contained"
                onClick={handleConnectGithub}
                disabled={connectingGithub}
                sx={{
                  backgroundColor: '#24292f',
                  '&:hover': { backgroundColor: '#3a4149' },
                }}
              >
                {connectingGithub ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Install GitHub App'
                )}
              </Button>
              <Alert severity="info" sx={{ mt: 2 }}>
                Click "Install GitHub App" to choose the repositories to connect. After installing,
                pull requests that name a scope's Jira key link to that scope automatically.
              </Alert>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* JIRA Integration */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>JIRA Integration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure organization-wide JIRA credentials. Members without their own JIRA settings will use these credentials.
            Individual members can override these by configuring their own JIRA credentials in User Settings.
          </Typography>

          {loadingJiraSettings ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading JIRA settings...</Typography>
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 2 }}>
              {jiraSettings.isConfigured && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip label="Configured" color="success" size="small" />
                  <Typography variant="body2">
                    Connected to <strong>{jiraSettings.url}</strong> as {jiraSettings.username}
                  </Typography>
                </Box>
              )}

              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="JIRA URL"
                  value={jiraForm.url}
                  onChange={(e) => setJiraForm(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://your-org.atlassian.net"
                  fullWidth
                  size="small"
                  helperText="Your Atlassian JIRA instance URL"
                />
                <TextField
                  label="Username (Email)"
                  value={jiraForm.username}
                  onChange={(e) => setJiraForm(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="admin@your-org.com"
                  fullWidth
                  size="small"
                  helperText="Email address of the JIRA account"
                />
                <TextField
                  label="API Token"
                  type="password"
                  value={jiraForm.apiToken}
                  onChange={(e) => setJiraForm(prev => ({ ...prev, apiToken: e.target.value }))}
                  placeholder={jiraSettings.isConfigured ? '••••••••••••••••' : 'Enter API token'}
                  fullWidth
                  size="small"
                  helperText={jiraSettings.isConfigured
                    ? 'Leave empty to keep existing token, or enter a new token to update'
                    : 'Generate an API token from id.atlassian.com'
                  }
                />
                <TextField
                  label="Default Project Key (Optional)"
                  value={jiraForm.projectKey}
                  onChange={(e) => setJiraForm(prev => ({ ...prev, projectKey: e.target.value }))}
                  placeholder="PROJ"
                  fullWidth
                  size="small"
                  helperText="Default project for JIRA searches"
                />
                <TextField
                  label="Story Point Field ID (Optional)"
                  value={jiraForm.storyPointFieldId}
                  onChange={(e) => setJiraForm(prev => ({ ...prev, storyPointFieldId: e.target.value }))}
                  placeholder="customfield_10016"
                  fullWidth
                  size="small"
                  helperText="Custom field ID for story points"
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveJira}
                    disabled={savingJira || (!jiraForm.apiToken && !jiraSettings.isConfigured) || !jiraForm.url || !jiraForm.username}
                  >
                    {savingJira ? <CircularProgress size={24} /> : (jiraSettings.isConfigured ? 'Update JIRA Settings' : 'Save JIRA Settings')}
                  </Button>
                  {jiraSettings.isConfigured && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleClearJira}
                      disabled={clearingJira}
                    >
                      {clearingJira ? <CircularProgress size={24} /> : 'Clear JIRA Settings'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
          </>
        )}

        {currentTab === 'danger' && isOwner && (
          <Box>
            <Typography variant="h6" color="error" gutterBottom>Danger Zone</Typography>
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main' }}>
              <Typography variant="body1" gutterBottom>
                Delete this organization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Once you delete an organization, there is no going back. All teams and associated data will be permanently deleted.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete Organization
              </Button>
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. This will permanently delete the organization
            <strong> {organization.name}</strong>, all teams, and remove all members.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Please type <strong>{organization.name}</strong> to confirm.
          </DialogContentText>
          <TextField
            fullWidth
            margin="dense"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={organization.name}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting || deleteConfirmText !== organization.name}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage || !!actionError}
        autoHideDuration={6000}
        onClose={() => { setSuccessMessage(null); setActionError(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={successMessage ? 'success' : 'error'}>
          {successMessage || actionError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationSettingsPage;
